import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Shows a spinner and blocks interaction. The label stays, so the control keeps its width. */
  isLoading?: boolean
  /** Rendered before the label, decorative. An icon-only button needs its own `aria-label`. */
  icon?: ReactNode
  iconAfter?: ReactNode
  isFullWidth?: boolean
  className?: string
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-text-on-primary hover:bg-primary-hover ' +
    'disabled:bg-primary/40 disabled:text-text-on-primary/70',
  secondary:
    'bg-surface-raised text-text border border-border ' +
    'hover:bg-surface-sunken hover:border-border-strong disabled:text-text-muted',
  ghost: 'text-text-secondary hover:bg-surface-sunken hover:text-text disabled:text-text-muted',
  danger: 'bg-danger text-surface-raised hover:brightness-95 disabled:bg-danger/40',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-2.5 text-[0.8125rem] gap-1.5 rounded-[0.375rem]',
  md: 'h-9 px-3.5 text-sm gap-2 rounded-control',
  lg: 'h-11 px-5 text-[0.9375rem] gap-2 rounded-control',
}

export function Button({
  variant = 'secondary',
  size = 'md',
  isLoading = false,
  icon,
  iconAfter,
  isFullWidth = false,
  className,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      // Loading is a kind of disabled, so a double-submit is impossible while in flight.
      disabled={disabled ?? isLoading}
      aria-busy={isLoading || undefined}
      data-variant={variant}
      className={cn(
        'inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap',
        'transition-[background-color,border-color,color,opacity] duration-150',
        'disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        isFullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {isLoading ? <Spinner size={size === 'sm' ? 12 : 14} /> : icon}
      {children}
      {!isLoading && iconAfter}
    </button>
  )
}
